import { parseFirst, toSql } from 'pgsql-ast-parser/lib';
import {
  Months,
  TimeSliderState,
} from '../../common/components/timeslider/types';
import { Domains, employeeIdDataField } from '../../constants';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
} from '../../types';
import { DataService } from '../data/service';
import { EMPLOYMENT_TEMPORALITY } from '../data/types';
import {
  dataTypeToExpr,
  employmentTemporalitySql,
  fieldToSql,
} from '../data/utils';
import { escapeSqlString, SQL } from '../data/utils-sql';
import { AuthenticatedBackendService } from '../service';
import { HasDependencies, hashString, SQLQuery, VersionId } from '../types';
import { EmployeeNotFoundError } from './errors';
import { Employee, EmployeeId, EmployeeProfileData } from './types';
import { employeeProfileTypeDataMapper, employeeTypeDataMapper } from './utils';

export interface EmployeeService extends HasDependencies {
  search: (
    searchQuery: SQLQuery,
    version: VersionId | null
  ) => Promise<Employee[]>;
  profileData: (
    employeeId: EmployeeId,
    version: VersionId,
    fields: DataFieldWithDataType[],
    valueFormatter: (
      field: DataFieldWithDataType,
      value: string | undefined
    ) => string | null
  ) => Promise<EmployeeProfileData>;
  list: (
    employeeIds: string[],
    timeSliderState?: TimeSliderState,
    firstMonthOfYear?: Months
  ) => Promise<Employee[]>;
}

export class BackendEmployeeService implements EmployeeService {
  constructor(
    readonly dataService: DataService,
    readonly backendService: AuthenticatedBackendService,
    readonly domain: Domains
  ) {}

  public getDeps = () => [
    this.domain,
    hashString([...this.backendService.getDeps()]),
  ];

  public list = async (
    employeeIds: string[],
    timeSliderState?: TimeSliderState,
    firstMonthOfYear?: Months
  ): Promise<Employee[]> => {
    const result = await this.dataService.queryDataFieldValues(
      [
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.EMPLOYEE_ID,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.COMPANY_EMAIL,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.FIRST_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.LAST_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.FULL_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.LOCAL_FULL_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.LOCAL_FIRST_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.LOCAL_LAST_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.PREFERRED_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.JOB_TITLE,
        },
      ],
      timeSliderState,
      firstMonthOfYear,
      false,
      `${fieldToSql(employeeIdDataField)} IN (${employeeIds.map((id) => `'${id}'`).join(',')})`
    );

    return employeeTypeDataMapper(result ?? null);
  };

  public search = async (
    searchQuery: string,
    version: VersionId | null
  ): Promise<Employee[]> => {
    if (searchQuery.length < 2) {
      return [];
    }

    const escapedSearchQuery = escapeSqlString(searchQuery);
    const queryString = `
      with similarity_result as (
        select
            greatest(
                case when employee.employeeId = '${escapedSearchQuery}' then 1 else 0 end,
                case when employee.companyEmail = '${escapedSearchQuery}' then 1 else 0 end,
                coalesce(similarity(employee.fullName, '${escapedSearchQuery}'), 0),
                coalesce(similarity(employee.localFullName, '${escapedSearchQuery}'), 0),
                coalesce(similarity(employee.localFullNamePronunciation, '${escapedSearchQuery}'), 0),
                coalesce(similarity(employee.preferredName, '${escapedSearchQuery}'), 0),
                coalesce(similarity(employee.localPreferredName, '${escapedSearchQuery}'), 0),
                coalesce(similarity(employee.localPreferredNamePronunciation, '${escapedSearchQuery}'), 0)
            ) as similarity,
          employee.employeeId,
          employee.companyEmail,
          employee.firstName,
          employee.lastName,
          employee.fullName,
          employee.localFullName,
          employee.localFirstName,
          employee.localLastName,
          employee.preferredName,
          employee.localPreferredName,
          employee.jobTitle
        from employee
        where
          ${version ? `employee.versionId = '${version}'` : ''}
          and employee.employmentTemporality in ('PAST', 'PRESENT')
          and employee.namespace = '${this.domain}'
        order by
          similarity desc
    ) select
      employeeId,
      companyEmail,
      firstName,
      lastName,
      fullName,
      localFullName,
      localFirstName,
      localLastName,
      preferredName,
      localPreferredName,
      jobTitle,
      similarity
    from similarity_result
    where similarity > 0.1
    limit 15;
  `;

    // Check that the SQL is valid
    // parsing adds parenthesis that are not accepted for whitelisted queries
    parseFirst(queryString);

    return this.backendService.fetchDataWhitelistedQuery(
      employeeTypeDataMapper
    )(queryString);
  };

  public profileData = async (
    employeeId: EmployeeId,
    version: VersionId,
    fields: DataFieldWithDataType[],
    valueFormatter: (
      field: DataFieldWithDataType,
      value: string | undefined
    ) => string | null
  ) => {
    const selection = fields
      .map((dimension) => fieldToSql(dimension))
      .join(',');

    const from = toSql.expr(dataTypeToExpr(DataTypes.EMPLOYEE));

    const employeeIdSelection = `${fieldToSql({
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.EMPLOYEE_ID,
    })} = '${employeeId}'`;
    const versionIdSelection = `${fieldToSql({
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.VERSION_ID,
    })} = '${version}'`;
    const employmentTemporalitySelection = employmentTemporalitySql([
      EMPLOYMENT_TEMPORALITY.PRESENT,
      EMPLOYMENT_TEMPORALITY.PAST,
      EMPLOYMENT_TEMPORALITY.FUTURE,
    ]);
    const where = [
      employeeIdSelection,
      versionIdSelection,
      employmentTemporalitySelection,
    ];
    const statementAsString = SQL.queryToString({
      select: selection,
      from,
      where,
      groupBy: selection,
    });
    const results = await this.backendService.fetchDataCustomSqlQuery((data) =>
      employeeProfileTypeDataMapper(data, fields, valueFormatter)
    )(statementAsString);
    const employee = results[0];
    if (!employee) {
      throw new EmployeeNotFoundError(employeeId);
    }
    return employee;
  };
}
